import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import {
  AlertTriangle, AlarmClock, CalendarClock, Check, ChevronDown, CloudOff, Eye, EyeOff, Loader2, Minimize2, Reply, RefreshCw, Save, Send, Signature, Sparkles, Wand2,
} from 'lucide-react';
import { replySubject, type Draft, type Mailbox, type MessageView, type ThreadSummary } from '@mailpilot/shared';
import { api, ApiError } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fromLocalInput, parseAddressList, toLocalInput, isValidEmail, displayName } from '../../lib/format';
import { useMailboxes } from '../../hooks/useMailboxes';
import { useOnline } from '../../hooks/useBrowser';
import { useDraftEditor, isEditableDraft, type ComposeFields } from '../../hooks/useDraftEditor';
import { Button } from '../ui/Button';
import { DraftStatusChip } from '../ui/Badge';
import { Menu } from '../ui/Overlay';
import { useToast } from '../ui/Toast';
import { HtmlFrame } from '../ui/HtmlFrame';
import { spring } from '../ui/motion';
import { AiStateView, type AiPhase } from './AiState';
import { AiSuggestionPanel } from './AiSuggestionPanel';
import { DraftStatusList } from './DraftStatusList';
import { useCurrentWorkspace } from '../../hooks/useWorkspace';
import { useReplyPresence } from '../../hooks/useTeam';
import { useSettings } from '../../hooks/useMe';

const TONES = ['Friendly', 'Formal', 'Concise', 'Warm', 'Direct', 'Apologetic'];

export interface ComposerHandle {
  focus: () => void;
}

interface Props {
  mode: 'reply' | 'new';
  thread?: ThreadSummary;
  messages?: MessageView[];
  drafts?: Draft[];
  onEvidence?: (messageId: string) => void;
  onSent?: (d: Draft) => void;
  /** Start in a collapsed "Reply" bar (reply mode only). */
  startCollapsed?: boolean;
}

function lastIncoming(messages: MessageView[] | undefined): MessageView | undefined {
  if (!messages?.length) return undefined;
  for (let i = messages.length - 1; i >= 0; i--) if (!messages[i].isOutgoing) return messages[i];
  return messages[messages.length - 1];
}

const inputCls = 'h-9 min-w-0 flex-1 bg-transparent text-[0.8125rem] text-ink placeholder:text-subtle focus:outline-none';
const rowLabel = 'w-14 shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle';

/** Icon-only toolbar button with a CSS tooltip (no native title, so the tooltip isn't doubled). */
const ToolIcon = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: ReactNode; active?: boolean; tipAlign?: 'end' }>(function ToolIcon(
  { label, icon, active, tipAlign, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      data-tip={label}
      className={clsx(
        'tip inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40',
        tipAlign === 'end' && 'tip-bottom-end',
        active ? 'bg-primary-soft text-primary-ink' : 'text-muted hover:bg-raised hover:text-ink',
        className,
      )}
      {...rest}
    >
      {icon}
    </button>
  );
});

function Separator() {
  return <span className="mx-1 hidden h-5 w-px bg-line sm:block" aria-hidden />;
}

export const Composer = forwardRef<ComposerHandle, Props>(function Composer({ mode, thread, messages, drafts = [], onEvidence, onSent, startCollapsed }, ref) {
  const qc = useQueryClient();
  const toast = useToast();
  const online = useOnline();
  const mbs = useMailboxes();
  const mailboxes = mbs.data?.mailboxes ?? [];
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [expanded, setExpanded] = useState(!startCollapsed);
  const [showCc, setShowCc] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // "Remind me if no reply" (days), applied to the conversation after a reply is sent.
  const [followUpDays, setFollowUpDays] = useState<number | null>(null);
  const undoSeconds = useSettings()?.undoSendSeconds ?? 10;
  const [scheduleAt, setScheduleAt] = useState('');
  const [sigPreview, setSigPreview] = useState(false);
  const [offerCorrection, setOfferCorrection] = useState<Draft | null>(null);

  const anchor = lastIncoming(messages);
  const editable = useMemo(() => drafts.filter(isEditableDraft).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null, [drafts]);
  const outgoing = useMemo(() => drafts.filter((d) => !isEditableDraft(d) && d.status !== 'cancelled'), [drafts]);

  const defaultMailbox = (): string => {
    if (mode === 'reply' && thread) return thread.mailboxId;
    return mailboxes.find((m) => m.isDefaultSender)?.id ?? mailboxes[0]?.id ?? '';
  };
  const defaults = (): ComposeFields => {
    const to = anchor ? (anchor.isOutgoing ? anchor.to : [anchor.from]) : [];
    return {
      mailboxId: defaultMailbox(),
      to: to.map((a) => (a.name ? `${a.name} <${a.email}>` : a.email)).join(', '),
      cc: '',
      subject: thread ? replySubject(thread.subject) : '',
      body: '',
      signatureId: null,
    };
  };

  const ed = useDraftEditor({
    storageKey: `mp:compose:${thread?.id ?? 'new'}`,
    threadId: thread?.id ?? null,
    inReplyToMessageId: anchor?.id ?? null,
    initialDraft: editable,
    defaults: defaults(),
    online,
  });
  const { fields, setFields, draft } = ed;

  // the mailbox list may load after mount (new message)
  useEffect(() => {
    if (!fields.mailboxId && mailboxes.length) setFields({ mailboxId: defaultMailbox() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxes.length]);

  // a newer server copy of our draft arrived (e.g. edited on another device) and we have no local edits
  useEffect(() => {
    if (editable && draft && editable.id === draft.id && editable.version > draft.version && ed.saveState !== 'local' && ed.saveState !== 'saving') ed.reloadFrom(editable);
    if (editable && !draft) ed.reloadFrom(editable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable?.id, editable?.version]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      setExpanded(true);
      setTimeout(() => bodyRef.current?.focus(), 60);
    },
  }));

  // ----- signatures -----
  const sigs = useQuery({ queryKey: qk.signatures, queryFn: () => api('GET /api/signatures'), staleTime: 300_000 });
  const availableSigs = (sigs.data?.signatures ?? []).filter((s) => !s.mailboxId || s.mailboxId === fields.mailboxId);
  const pickDefaultSig = (mailboxId: string) => {
    const list = (sigs.data?.signatures ?? []).filter((s) => !s.mailboxId || s.mailboxId === mailboxId);
    const flag = mode === 'reply' ? 'isDefaultReply' : 'isDefaultNew';
    return (list.find((s) => s.mailboxId === mailboxId && s[flag]) ?? list.find((s) => !s.mailboxId && s[flag]) ?? null)?.id ?? null;
  };
  useEffect(() => {
    if (sigs.data && fields.signatureId === null && !draft) {
      const id = pickDefaultSig(fields.mailboxId);
      if (id) setFields({ signatureId: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigs.data, fields.mailboxId]);
  const sig = availableSigs.find((s) => s.id === fields.signatureId) ?? null;

  // ----- AI -----
  const [aiError, setAiError] = useState<unknown>(null);
  const [lastAiAction, setLastAiAction] = useState<null | (() => void)>(null);
  const suggest = useMutation({
    mutationFn: () => api('POST /api/threads/:id/suggest', { params: { id: thread!.id }, body: {} }),
    onSuccess: (r) => {
      ed.adopt(r.draft, true);
      setAiError(null);
      setExpanded(true);
    },
    onError: (e) => setAiError(e),
  });
  const rewrite = useMutation({
    mutationFn: async (v: { action: 'shorten' | 'tone' | 'regenerate'; tone?: string }) => {
      const saved = await ed.flush();
      const id = saved?.id ?? draft?.id;
      if (!id) throw new ApiError(400, 'no_draft', 'Write something or ask for a suggestion first.');
      return api('POST /api/drafts/:id/rewrite', { params: { id }, body: v });
    },
    onSuccess: (r) => {
      ed.adopt(r.draft, true);
      setAiError(null);
    },
    onError: (e) => setAiError(e),
  });
  const aiBusy = suggest.isPending || rewrite.isPending;
  const isAi = !!draft && (draft.kind === 'suggestion' || !!draft.explanation);
  const aiPhase: AiPhase = aiBusy ? 'thinking' : aiError ? 'error' : isAi ? 'result' : 'idle';
  const runSuggest = () => {
    setLastAiAction(() => () => suggest.mutate());
    suggest.mutate();
  };
  const runRewrite = (action: 'shorten' | 'tone' | 'regenerate', tone?: string) => {
    setLastAiAction(() => () => rewrite.mutate({ action, tone }));
    rewrite.mutate({ action, tone });
  };

  // ----- send -----
  const generatedBodyRef = useRef<string | null>(null);
  useEffect(() => {
    if (draft?.kind === 'suggestion' && !ed.saveState.match(/local|saving/)) generatedBodyRef.current ??= draft.bodyText;
  }, [draft, ed.saveState]);
  useEffect(() => {
    // remember the AI's original text whenever a fresh suggestion/rewrite is adopted
    if (suggest.data) generatedBodyRef.current = suggest.data.draft.bodyText;
  }, [suggest.data]);
  useEffect(() => {
    if (rewrite.data) generatedBodyRef.current = rewrite.data.draft.bodyText;
  }, [rewrite.data]);

  const toList = parseAddressList(fields.to);
  const invalidRecipients = [...toList, ...parseAddressList(fields.cc)].filter((a) => !isValidEmail(a.email));
  const send = useMutation({
    mutationFn: async (scheduledAt: string | null) => {
      const saved = await ed.flush();
      if (!saved) throw new ApiError(400, 'empty', 'Write a message first.');
      const res = await api('POST /api/drafts/:id/send', { params: { id: saved.id }, body: { scheduledAt, signatureId: fields.signatureId, undoSeconds: scheduledAt ? 0 : undoSeconds } });
      if (followUpDays && thread) await api('POST /api/threads/:id/follow-up', { params: { id: thread.id }, body: { days: followUpDays } }).catch(() => undefined);
      return { res, saved };
    },
    onSuccess: ({ res, saved }) => {
      const edited = saved.kind === 'suggestion' && generatedBodyRef.current !== null && generatedBodyRef.current.trim() !== saved.bodyText.trim();
      if (edited) setOfferCorrection(res.draft);
      const reminder = followUpDays ? ` We'll remind you if there's no reply in ${followUpDays === 1 ? 'a day' : followUpDays === 7 ? 'a week' : `${followUpDays} days`}.` : '';
      if (res.undoUntil) {
        const ms = Math.max(1000, Date.parse(res.undoUntil) - Date.now());
        toast.info(`Sending in ${Math.round(ms / 1000)} seconds.${reminder}`, { label: 'Undo', onClick: () => undoSend.mutate(res.draft.id) }, { durationMs: ms });
      } else {
        toast.success((res.draft.status === 'scheduled' ? 'Scheduled.' : res.draft.status === 'pending_attachment' ? 'Waiting for the requested attachment before sending.' : 'Queued — MailPilot re-checks the thread right before sending.') + reminder);
      }
      setFollowUpDays(null);
      generatedBodyRef.current = null;
      ed.reset(defaults());
      setScheduleOpen(false);
      setExpanded(mode === 'new');
      if (thread) void qc.invalidateQueries({ queryKey: qk.thread(thread.id) });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      onSent?.(res.draft);
    },
    onError: (e) => toast.error(e),
  });
  const undoSend = useMutation({
    mutationFn: (draftId: string) => api('POST /api/drafts/:id/undo-send', { params: { id: draftId } }),
    onSuccess: () => {
      toast.success('Not sent — your message is back in your drafts.');
      if (thread) {
        void api('POST /api/threads/:id/follow-up', { params: { id: thread.id }, body: { days: null } }).catch(() => undefined);
        void qc.invalidateQueries({ queryKey: qk.thread(thread.id) });
      }
      void qc.invalidateQueries({ queryKey: qk.draftsRoot });
    },
    onError: (e) => toast.error(e),
  });
  const correction = useMutation({
    mutationFn: (v: { id: string; save: boolean }) => api('POST /api/drafts/:id/correction', { params: { id: v.id }, body: { saveAsExample: v.save } }),
    onSuccess: (_r, v) => {
      if (v.save) toast.success('Saved as a style example. You can review it in AI preferences.', { label: 'Open preferences', to: '/settings/preferences' });
      setOfferCorrection(null);
    },
    onError: (e) => toast.error(e),
  });

  const fromMailbox: Mailbox | undefined = mailboxes.find((m) => m.id === fields.mailboxId);
  const receivedBy = thread ? mailboxes.find((m) => m.id === thread.mailboxId) : undefined;
  const fromChanged = mode === 'reply' && thread && fields.mailboxId !== thread.mailboxId;
  // Organizations: teammates see "… is replying" while there's text in this reply.
  const { isOrg } = useCurrentWorkspace();
  useReplyPresence(mode === 'reply' && isOrg ? thread?.id : undefined, fields.body.trim().length > 0 && !send.isPending);
  const canSend = online && !!fields.mailboxId && toList.length > 0 && invalidRecipients.length === 0 && (fields.body.trim().length > 0) && !send.isPending && !aiBusy;

  const saveLabel = {
    idle: '',
    local: online ? 'Saving…' : 'Saved on this device',
    saving: 'Saving…',
    saved: 'Draft saved',
    error: 'Not saved to server — kept on this device',
    conflict: 'Changed on another device',
  }[ed.saveState];

  const openEditor = () => {
    setExpanded(true);
    setTimeout(() => bodyRef.current?.focus(), 60);
  };

  if (mode === 'reply' && !expanded) {
    const replyTo = anchor ? displayName(anchor.isOutgoing ? anchor.to[0] ?? anchor.from : anchor.from) : null;
    return (
      <div className="space-y-3">
        <DraftStatusList drafts={outgoing} threadId={thread?.id ?? null} />
        {offerCorrection && <CorrectionOffer onSave={() => correction.mutate({ id: offerCorrection.id, save: true })} onDismiss={() => correction.mutate({ id: offerCorrection.id, save: false })} busy={correction.isPending} />}
        <div className="flex flex-wrap items-center gap-3 rounded-card border border-hairline bg-surface p-2 pl-3 shadow-card">
          <button
            type="button"
            onClick={openEditor}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-xl py-1.5 text-left text-[0.875rem] text-muted transition-colors hover:text-ink"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-raised text-muted">
              <Reply className="h-4 w-4" aria-hidden />
            </span>
            <span className="truncate">
              {editable ? 'Continue your draft' : replyTo ? `Reply to ${replyTo}…` : 'Write a reply…'}
            </span>
          </button>
          <div className="flex shrink-0 items-center gap-2">
            {thread && (
              <Button variant="soft" icon={<Sparkles className="h-4 w-4" />} loading={suggest.isPending} onClick={runSuggest}>
                Suggest reply
              </Button>
            )}
            <Button variant="primary" icon={<Reply className="h-4 w-4" />} onClick={openEditor}>
              Reply
            </Button>
          </div>
        </div>
        {aiError !== null && <AiStateView phase="error" error={aiError} onRetry={runSuggest} idle={null} result={null} thinkingLabel="" />}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <DraftStatusList drafts={outgoing} threadId={thread?.id ?? null} />
      {offerCorrection && <CorrectionOffer onSave={() => correction.mutate({ id: offerCorrection.id, save: true })} onDismiss={() => correction.mutate({ id: offerCorrection.id, save: false })} busy={correction.isPending} />}

      <motion.section
        aria-label={mode === 'reply' ? 'Reply' : 'New message'}
        initial={startCollapsed ? { opacity: 0, y: 10 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={spring}
        className="overflow-hidden rounded-card border border-hairline bg-surface shadow-card focus-within:border-primary/30"
      >
        {/* header fields */}
        <div className="divide-y divide-line/60 border-b border-line/60 px-5">
          <div className="flex items-center gap-3 py-1.5">
            <label htmlFor="cmp-from" className={rowLabel}>From</label>
            <div className="relative flex min-w-0 flex-1 items-center gap-2">
              {fromMailbox && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: fromMailbox.color }} aria-hidden />}
              <select
                id="cmp-from"
                value={fields.mailboxId}
                onChange={(e) => setFields({ mailboxId: e.target.value, signatureId: pickDefaultSig(e.target.value) })}
                className="h-9 min-w-0 flex-1 cursor-pointer appearance-none bg-transparent pr-6 text-[0.8125rem] text-ink focus:outline-none"
              >
                {mailboxes.length === 0 && <option value="">No mailboxes connected</option>}
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.senderName ? `${m.senderName} <${m.email}>` : m.email}
                    {m.isDemo ? ' (DEMO)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-0 h-4 w-4 text-muted" aria-hidden />
            </div>
            {draft && <DraftStatusChip status={draft.status} />}
          </div>
          <div className="flex items-center gap-3 py-1">
            <label htmlFor="cmp-to" className={rowLabel}>To</label>
            <input id="cmp-to" className={inputCls} value={fields.to} onChange={(e) => setFields({ to: e.target.value })} placeholder="name@example.com" autoComplete="off" />
            {!showCc && !fields.cc && (
              <button type="button" className="rounded-md px-1.5 py-0.5 text-xs font-medium text-muted transition-colors hover:bg-raised hover:text-ink" onClick={() => setShowCc(true)}>
                Cc
              </button>
            )}
          </div>
          {(showCc || fields.cc) && (
            <div className="flex items-center gap-3 py-1">
              <label htmlFor="cmp-cc" className={rowLabel}>Cc</label>
              <input id="cmp-cc" className={inputCls} value={fields.cc} onChange={(e) => setFields({ cc: e.target.value })} autoComplete="off" />
            </div>
          )}
          {mode === 'new' && (
            <div className="flex items-center gap-3 py-1">
              <label htmlFor="cmp-subject" className={rowLabel}>Subject</label>
              <input id="cmp-subject" className={clsx(inputCls, 'font-medium')} value={fields.subject} onChange={(e) => setFields({ subject: e.target.value })} />
            </div>
          )}
        </div>

        {fromChanged && (
          <div role="alert" className="mx-5 mt-3 flex items-start gap-2 rounded-xl border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              This email was received by <b>{receivedBy?.email ?? 'another mailbox'}</b>, but you're replying from <b>{fromMailbox?.email}</b>. The recipient will see a different sender.{' '}
              <button type="button" className="font-semibold underline" onClick={() => thread && setFields({ mailboxId: thread.mailboxId, signatureId: pickDefaultSig(thread.mailboxId) })}>
                Use {receivedBy?.email}
              </button>
            </span>
          </div>
        )}
        {invalidRecipients.length > 0 && <p className="mx-5 mt-2 text-xs text-danger-ink">Check these addresses: {invalidRecipients.map((a) => a.email).join(', ')}</p>}

        <div className="px-5 pt-3">
          <textarea
            ref={bodyRef}
            aria-label="Message body"
            value={fields.body}
            onChange={(e) => setFields({ body: e.target.value })}
            placeholder={mode === 'reply' ? 'Write your reply…' : 'Write your message…'}
            disabled={aiBusy}
            className="reading min-h-[180px] w-full resize-y bg-transparent text-ink placeholder:text-subtle focus:outline-none disabled:opacity-60"
          />
        </div>

        {/* signature line */}
        <div className="px-5 pb-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
            <Signature className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {sig ? (
              <>
                <span>
                  Signature <span className="font-medium text-ink">{sig.name}</span> is added once when sent.
                </span>
                <button
                  type="button"
                  onClick={() => setSigPreview((p) => !p)}
                  aria-expanded={sigPreview}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-primary-ink transition-colors hover:bg-primary-soft"
                >
                  {sigPreview ? <EyeOff className="h-3 w-3" aria-hidden /> : <Eye className="h-3 w-3" aria-hidden />}
                  {sigPreview ? 'Hide preview' : 'Preview'}
                </button>
              </>
            ) : (
              <span>No signature — choose one from the toolbar.</span>
            )}
          </div>
          <AnimatePresence initial={false}>
            {sig && sigPreview && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={spring} className="overflow-hidden">
                <div className="mt-2 rounded-xl border border-dashed border-line p-2">
                  {sig.html ? <HtmlFrame html={sig.html} title="Signature preview" minHeight={40} maxHeight={160} className="!border-0" /> : <pre className="whitespace-pre-wrap font-sans text-xs text-muted">{sig.text}</pre>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* AI */}
        {thread && (aiPhase !== 'idle') && (
          <div className="px-5 pb-4">
            <AiStateView
              phase={aiPhase}
              error={aiError}
              onRetry={() => (lastAiAction ? lastAiAction() : runSuggest())}
              thinkingLabel={rewrite.isPending ? 'Rewriting…' : 'Drafting a reply from this conversation…'}
              idle={null}
              result={draft && isAi ? <AiSuggestionPanel draft={draft} onEvidence={onEvidence} /> : null}
            />
          </div>
        )}

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-1 border-t border-line/60 bg-raised/40 px-3 py-2.5">
          {thread && (
            <>
              <Button variant="soft" size="sm" icon={<Sparkles className="h-4 w-4" />} onClick={runSuggest} disabled={aiBusy} className="!rounded-xl">
                {isAi ? 'New suggestion' : 'Suggest reply'}
              </Button>
              <Separator />
            </>
          )}
          <Menu
            label="Change tone"
            width={180}
            trigger={(p) => <ToolIcon {...p} label="Change tone" icon={<Wand2 className="h-[18px] w-[18px]" />} disabled={aiBusy || !fields.body.trim()} />}
            items={TONES.map((t) => ({ key: t, label: t, onSelect: () => runRewrite('tone', t.toLowerCase()) }))}
          />
          <ToolIcon label="Shorten" icon={<Minimize2 className="h-[18px] w-[18px]" />} onClick={() => runRewrite('shorten')} disabled={aiBusy || !fields.body.trim()} />
          <ToolIcon label="Regenerate" icon={<RefreshCw className="h-[18px] w-[18px]" />} onClick={() => runRewrite('regenerate')} disabled={aiBusy || !draft} />
          <Separator />
          <Menu
            label="Signature"
            width={220}
            trigger={(p) => <ToolIcon {...p} label={sig ? `Signature: ${sig.name}` : 'Signature'} icon={<Signature className="h-[18px] w-[18px]" />} active={!!sig} />}
            items={[
              { key: 'none', label: 'No signature', active: !fields.signatureId, onSelect: () => setFields({ signatureId: null }), trailing: !fields.signatureId ? <Check className="h-4 w-4" aria-hidden /> : undefined },
              ...availableSigs.map((s) => ({
                key: s.id,
                label: s.name,
                active: s.id === fields.signatureId,
                onSelect: () => setFields({ signatureId: s.id }),
                trailing: s.id === fields.signatureId ? <Check className="h-4 w-4" aria-hidden /> : undefined,
              })),
            ]}
          />
          {thread && (
            <Menu
              label="Remind me if no reply"
              width={240}
              header={<p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">Remind me if no reply</p>}
              trigger={(p) => <ToolIcon {...p} label={followUpDays ? `Reminder: ${followUpDays === 1 ? '1 day' : followUpDays === 7 ? '1 week' : `${followUpDays} days`}` : 'Remind me if no reply'} icon={<AlarmClock className="h-[18px] w-[18px]" />} active={!!followUpDays} />}
              items={[
                { key: 'off', label: "Don't remind me", active: !followUpDays, onSelect: () => setFollowUpDays(null), trailing: !followUpDays ? <Check className="h-4 w-4" aria-hidden /> : undefined },
                ...[
                  { d: 1, label: 'In 1 day' },
                  { d: 3, label: 'In 3 days' },
                  { d: 7, label: 'In 1 week' },
                ].map((o) => ({ key: String(o.d), label: o.label, active: followUpDays === o.d, onSelect: () => setFollowUpDays(o.d), trailing: followUpDays === o.d ? <Check className="h-4 w-4" aria-hidden /> : undefined })),
              ]}
            />
          )}
          <ToolIcon label="Schedule send" icon={<CalendarClock className="h-[18px] w-[18px]" />} onClick={() => setScheduleOpen((o) => !o)} aria-expanded={scheduleOpen} active={scheduleOpen} disabled={!canSend && !scheduleOpen} />

          <span className="ml-auto flex items-center gap-1.5 pr-1 text-[0.6875rem] text-muted" aria-live="polite">
            {ed.saveState === 'saving' && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
            {!online && <CloudOff className="h-3 w-3" aria-hidden />}
            {saveLabel}
          </span>
          <ToolIcon label="Save draft" tipAlign="end" icon={<Save className="h-[18px] w-[18px]" />} onClick={() => ed.flush().then((d) => d && toast.success('Draft saved.')).catch((e) => toast.error(e))} disabled={!online} />
          <Button
            variant="primary"
            size="sm"
            icon={<Send className="h-4 w-4" />}
            loading={send.isPending}
            disabled={!canSend}
            onClick={() => send.mutate(null)}
            className="ml-1 !h-9 !rounded-xl !px-4"
            title={!online ? 'Offline — your draft is saved; send when you reconnect' : undefined}
          >
            Send
          </Button>
        </div>
        <AnimatePresence>
          {scheduleOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={spring} className="overflow-hidden">
              <div className="flex flex-wrap items-end gap-3 border-t border-line/60 px-5 py-3">
                <div className="flex flex-col gap-1">
                  <label htmlFor="cmp-sched" className="text-xs font-medium text-muted">Send at</label>
                  <input
                    id="cmp-sched"
                    type="datetime-local"
                    min={toLocalInput(new Date(Date.now() + 60_000).toISOString())}
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    className="h-10 rounded-xl border border-line bg-surface px-3 text-[0.8125rem] text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                </div>
                <Button variant="primary" size="md" icon={<CalendarClock className="h-4 w-4" />} disabled={!scheduleAt || !fromLocalInput(scheduleAt) || new Date(scheduleAt).getTime() < Date.now()} loading={send.isPending} onClick={() => send.mutate(fromLocalInput(scheduleAt))}>
                  Schedule send
                </Button>
                <p className="w-full text-xs text-muted">The server re-checks the thread at send time and cancels the reply if someone already answered.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {!online && <p className="border-t border-line/60 bg-warn-soft/60 px-5 py-2 text-xs text-warn-ink">You're offline. Your draft is saved on this device and syncs when you reconnect.</p>}
        {ed.saveState === 'conflict' && (
          <p className="border-t border-line/60 bg-warn-soft/60 px-5 py-2 text-xs text-warn-ink">
            This draft changed on another device.{' '}
            <button type="button" className="font-semibold underline" onClick={() => thread && void qc.invalidateQueries({ queryKey: qk.thread(thread.id) })}>
              Load the latest version
            </button>
          </p>
        )}
      </motion.section>
    </div>
  );
});

function CorrectionOffer({ onSave, onDismiss, busy }: { onSave: () => void; onDismiss: () => void; busy: boolean }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={spring} className="flex flex-wrap items-center gap-3 rounded-card border border-primary/20 bg-primary-soft/60 px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-fg">
        <Sparkles className="h-4 w-4" aria-hidden />
      </span>
      <p className="min-w-0 flex-1 text-[0.8125rem] leading-relaxed text-ink">You edited the AI draft before sending. Save your edits as a style example so future suggestions sound more like you?</p>
      <Button size="sm" variant="primary" loading={busy} onClick={onSave}>
        Save as style example
      </Button>
      <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy}>
        No thanks
      </Button>
    </motion.div>
  );
}
